import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await api.get(`/news/${id}`);
        setNews(response.data);
      } catch (err) {
        console.error('ニュースの取得に失敗しました:', err);
        setError('ニュースが見つかりませんでした');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [id]);

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader />

      {/* 戻るボタン（ヘッダー下に浮かせる） */}
      <div className="fixed top-3 right-4 sm:top-5 sm:right-6 z-[60]">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
          data-testid="news-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          戻る
        </Button>
      </div>

      {/* 本文 */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20 relative">
        {loading ? (
          <div className="text-center text-gray-600 py-20">読み込み中...</div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-400 mb-6">{error}</p>
            <Link
              to="/"
              className="inline-flex items-center text-amber-500 hover:text-amber-500"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              トップに戻る
            </Link>
          </div>
        ) : news && (
          <article data-testid="news-detail-article">
            {news.image_url && (
              <div className="rounded-2xl overflow-hidden mb-8 border border-amber-500/20">
                <img
                  src={news.image_url}
                  alt={news.title}
                  className="w-full h-auto max-h-[500px] object-cover"
                />
              </div>
            )}

            <div className="flex items-center space-x-3 mb-6 flex-wrap gap-y-2">
              <span className="px-3 py-1 bg-amber-500/20 text-amber-500 text-xs rounded-full border border-amber-500/30">
                {news.category}
              </span>
              <span className="text-gray-500 text-sm">{news.date}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-amber-700 to-amber-500 bg-clip-text text-transparent leading-tight">
              {news.title}
            </h1>

            {news.excerpt && (
              <p className="text-lg text-gray-600 mb-8 p-4 border-l-4 border-amber-500/50 bg-amber-500/5 rounded-r-lg">
                {news.excerpt}
              </p>
            )}

            <div className="prose prose-amber max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
              {news.content}
            </div>

            <div className="mt-12 pt-8 border-t border-amber-500/20">
              <Link
                to="/"
                className="inline-flex items-center text-amber-500 hover:text-amber-500 transition-colors"
                data-testid="news-return-home"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                ニュース一覧に戻る
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
};

export default NewsDetailPage;
